import {
  DownloadSimpleIcon,
  EyeIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  PaperPlaneTiltIcon,
  PencilSimpleIcon,
  PlusIcon,
} from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Manager, useGetManagers } from '@/api/managers'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { RowAction } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { popupFactory, toastFactory } from '@/factories'
import { useDesignVersionToggle, useFilters, useSearchDraft, useSearchParams } from '@/hooks'
import { getApiErrorMessage } from '@/utils/error'

import { CreatedDateRangePicker } from '../shared/created-date-range-picker'
import { exportCsv } from '../shared/export-csv'
import { filterByDateRange, paginateItems } from '../shared/person-table.utils'
import { useAccountEmailActions } from '../shared/use-account-email-actions'
import { useDetailsDeepLink } from '../shared/use-details-deep-link'
import { ManagerDetailsModal } from './manager-details-modal'
import { ManagerFormModal } from './manager-form-modal'
import {
  getManagersTableColumns,
  MANAGERS_TABLE_ID,
  managersTableFiltersSchema,
  managersTableInitialFilters,
} from './managers.constants'
import { filterManagers, getManagersCsvFields, sortManagers } from './managers.utils'

export const ManagersPage = () => {
  const { t } = useTranslation(['organisation'])
  const { handleSendPasswordReset, handleSendSetupLink } = useAccountEmailActions()

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'managers',
    screen: 'Managers',
    to: { path: '/dashboard/organisation/managers/legacy' },
    version: 'new',
  })

  const { data, error, isLoading } = useGetManagers()

  const searchParams = useSearchParams({
    initialState: managersTableInitialFilters,
    schema: managersTableFiltersSchema,
  })

  const tableFilters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: managersTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const { fromDate, limit, search, skip, sortBy, sortOrder, toDate } = tableFilters.filters

  const [searchDraft, handleSearchChange] = useSearchDraft(search, (value) =>
    tableFilters.setFilter('search', value),
  )

  const filteredManagers = useMemo(
    () =>
      sortManagers(
        filterManagers(
          filterByDateRange(data ?? [], fromDate, toDate, (manager) => manager.createdAt),
          search,
        ),
        sortBy,
        sortOrder,
      ),
    [data, fromDate, toDate, search, sortBy, sortOrder],
  )

  const pageManagers = useMemo(
    () => paginateItems(filteredManagers, skip, limit),
    [filteredManagers, skip, limit],
  )

  const columns = useMemo(() => getManagersTableColumns(t), [t])

  const handleCreate = () => {
    popupFactory.open(ManagerFormModal, { mode: 'create' })
  }

  const [isExporting, setIsExporting] = useState(false)

  const handleExportCsv = async () => {
    setIsExporting(true)
    try {
      await exportCsv({
        data: filteredManagers,
        fields: getManagersCsvFields(t),
        page: 'managers',
      })
    } catch (exportError) {
      toastFactory.error(getApiErrorMessage(exportError))
    } finally {
      setIsExporting(false)
    }
  }

  const handleSort = (nextSortBy: string | null, nextSortOrder: 'asc' | 'desc' | null) => {
    tableFilters.setFilters({
      sortBy: (nextSortBy as typeof sortBy) ?? undefined,
      sortOrder: nextSortOrder ?? undefined,
    })
  }

  const openDetails = useDetailsDeepLink(ManagerDetailsModal, (managerId: number) => ({
    managerId,
  }))

  const rowActions: RowAction<Manager>[] = useMemo(
    () => [
      {
        icon: EyeIcon,
        label: t('organisation:managers.list.rowActions.details'),
        onClick: (manager) => openDetails(manager.id),
        primary: true,
      },
      {
        icon: PencilSimpleIcon,
        label: t('organisation:managers.list.rowActions.edit'),
        onClick: (manager) => popupFactory.open(ManagerFormModal, { manager, mode: 'edit' }),
      },
      {
        icon: PaperPlaneTiltIcon,
        label: t('organisation:shared.accountEmails.sendSetupLink'),
        onClick: (manager) => handleSendSetupLink(manager.user.email),
      },
      {
        icon: KeyIcon,
        label: t('organisation:shared.accountEmails.sendPasswordReset'),
        onClick: (manager) => handleSendPasswordReset(manager.user.email),
      },
    ],
    [t, handleSendSetupLink, handleSendPasswordReset, openDetails],
  )

  if (redirect) return redirect

  return (
    <Page>
      <PageHeader
        title={t('organisation:managers.list.title')}
        actions={
          <>
            <ToggleSwitch
              falsyLabel={t('organisation:managers.list.designToggle.old')}
              truthyLabel={t('organisation:managers.list.designToggle.new')}
              checked={true}
              onChange={handleUIToggle}
            />

            <Button size="small" use="primary" onClick={handleCreate}>
              {t('organisation:managers.list.createNew')}
              <Icon icon={PlusIcon} />
            </Button>
          </>
        }
      />

      <TableGroup
        columns={columns}
        data={pageManagers}
        emptyNoFilters={{
          description: t('organisation:managers.list.empty.noFilters.description'),
          title: t('organisation:managers.list.empty.noFilters.title'),
        }}
        emptyWithFilters={{
          description: t('organisation:managers.list.empty.withFilters.description'),
          title: t('organisation:managers.list.empty.withFilters.title'),
        }}
        error={error}
        getRowKey={(manager) => manager.id}
        id={MANAGERS_TABLE_ID}
        loading={isLoading}
        navigation={{
          actions: (
            <Button
              onlyBox
              disabled={!filteredManagers.length}
              loading={isExporting}
              size="small"
              title={t('organisation:managers.list.exportCsv')}
              use="secondary"
              onClick={handleExportCsv}
            >
              <Icon icon={DownloadSimpleIcon} />
            </Button>
          ),
          datePicker: (
            <CreatedDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              onChange={(range) => tableFilters.setFilters(range)}
            />
          ),
          search: (
            <TextInput
              clearable
              icon={MagnifyingGlassIcon}
              name="search"
              placeholder={t('organisation:managers.list.search.placeholder')}
              size="small"
              value={searchDraft}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          ),
        }}
        pagination={{
          paginationFilters: tableFilters,
          total: filteredManagers.length,
        }}
        rowActions={rowActions}
        sortBy={sortBy ?? null}
        sortOrder={sortOrder ?? null}
        tableFilters={tableFilters}
        onSort={handleSort}
      />
    </Page>
  )
}
